import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Users, Hammer, Globe2, Target, Eye, Sparkles, Award, TreePine } from "lucide-react";
import {
  PHOTO_INVENTORY,
  PHOTO_MOVELARIA,
  PHOTO_BASE_KM117,
  COOMFLONA_TEAM,
  COOMFLONA_MANEJO,
  whatsappEcoLojaLink,
} from "../lib/constants";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[65vh] min-h-[460px] flex items-end overflow-hidden">
        <img src={PHOTO_INVENTORY} alt="Equipe da COOMFLONA em inventário florestal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900 via-forest-900/60 to-forest-900/20" />
        <div className="container-page relative pb-16 text-cream-100">
          <div className="eyebrow !text-copaiba-300 mb-4">Nossa história</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
            Floresta em pé, <span className="italic text-copaiba-300">comunidade forte</span>.
          </h1>
          <p className="mt-6 text-cream-100/85 max-w-2xl text-lg">
            21 anos de gestão comunitária dos recursos naturais na Floresta Nacional do Tapajós — Belterra, oeste do Pará.
          </p>
        </div>
      </section>

      {/* História oficial */}
      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Como tudo começou</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
              Uma cooperativa criada para proteger a floresta.
            </h2>
            <div className="section-divider my-7" />
            <p className="text-forest-900/70 text-sm uppercase tracking-[0.18em]">
              Fundada em 16 de fevereiro de 2005
            </p>
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg text-forest-900/85 leading-relaxed">
            <p>
              A <strong>Cooperativa Mista da Flona do Tapajós — COOMFLONA</strong> foi criada com o objetivo de
              <strong> minimizar a exploração ilegal dos recursos florestais</strong> nas comunidades localizadas na
              Floresta Nacional do Tapajós, em <strong>Belterra</strong>, oeste do Pará.
            </p>
            <p>
              Sua criação, em <strong>16 de fevereiro de 2005</strong>, foi motivada pelas lideranças locais, após
              debates e seminários sobre o assunto. Para que a proposta se materializasse, as lideranças contaram com o
              apoio do <strong>Projeto de Manejo Florestal Sustentável na Amazônia (Promanejo)</strong>.
            </p>
            <p>
              Passados 21 anos, a Cooperativa tem trabalhado para beneficiar <strong>mais de mil famílias</strong>
              inseridas na Flona do Tapajós, aumentando a renda familiar de forma direta e indireta por meio do
              <strong> manejo florestal sustentável madeireiro</strong> executado pelos próprios comunitários — que, ao
              longo do tempo, compreenderam que explorar os recursos naturais sem destruir a floresta, além de gerar
              renda, significa <strong>mais qualidade de vida para as comunidades</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Base KM 117 */}
      <section className="relative py-24 bg-cream-200 overflow-hidden">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Onde estamos</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
              Base KM 117 — nosso coração operacional na Flona.
            </h2>
            <div className="section-divider my-7" />
            <p className="text-forest-900/85 leading-relaxed text-lg">
              Cercada pela floresta, a base KM 117 é onde planejamos, acolhemos as equipes, realizamos treinamentos e
              coordenamos as operações de manejo florestal comunitário. É daqui que saímos para as áreas de trabalho e
              para onde voltamos, sempre com a certeza de que a floresta continua viva.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              <Highlight i={TreePine} t="Dentro da Flona" />
              <Highlight i={Users} t="305 cooperados" />
              <Highlight i={Globe2} t="Rastreabilidade total" />
            </div>
          </div>
          <div className="lg:col-span-6 aspect-square overflow-hidden">
            <img src={PHOTO_BASE_KM117} alt="Vista aérea da Base KM 117 da COOMFLONA" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Números */}
      <section className="bg-forest-900 text-cream-100 py-20 relative overflow-hidden">
        <img src={COOMFLONA_TEAM} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-forest-900/75" />
        <div className="container-page relative grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { k: "21", v: "anos de cooperativa" },
            { k: "305", v: "cooperados ativos" },
            { k: "1.000+", v: "famílias beneficiadas" },
            { k: "100%", v: "manejo comunitário" },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-display text-5xl md:text-6xl text-copaiba-300">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-cream-100/70 mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Missão, Visão, Valores */}
      <section className="py-24">
        <div className="container-page">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow mb-3">Quem somos</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
              Missão, visão e valores.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <article className="bg-white border border-cream-400 p-8 hover:bg-cream-200 transition-colors duration-300">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-forest-700 text-cream-100 text-xs uppercase tracking-[0.18em] mb-5">
                <Target className="w-3.5 h-3.5" /> Missão
              </div>
              <p className="text-forest-900/85 leading-relaxed">
                Promover o uso múltiplo da floresta amazônica por meio do manejo florestal comunitário de forma
                sustentável, oferecendo produtos e serviços de qualidade, gerando renda e melhorando a qualidade de vida
                das populações locais, preservando o meio ambiente e valorizando o trabalho e a cultura.
              </p>
            </article>
            <article className="bg-white border border-cream-400 p-8 hover:bg-cream-200 transition-colors duration-300">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-forest-700 text-cream-100 text-xs uppercase tracking-[0.18em] mb-5">
                <Eye className="w-3.5 h-3.5" /> Visão
              </div>
              <p className="text-forest-900/85 leading-relaxed">
                Ser uma cooperativa inserida no mercado de forma estável, possibilitando a transparência de conhecimentos
                e <strong>preservando a floresta para as futuras gerações</strong>.
              </p>
            </article>
            <article className="bg-white border border-cream-400 p-8 hover:bg-cream-200 transition-colors duration-300">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-forest-700 text-cream-100 text-xs uppercase tracking-[0.18em] mb-5">
                <Sparkles className="w-3.5 h-3.5" /> Valores
              </div>
              <ul className="space-y-2.5 text-forest-900/85">
                {["Ética", "Responsabilidade", "Comprometimento", "Transparência", "Inovação", "Excelência"].map((v) => (
                  <li key={v} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-copaiba-500" />
                    {v}
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Movelaria Anambé */}
      <section className="bg-forest-700 text-cream-100 py-24 relative overflow-hidden">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 aspect-[4/5] overflow-hidden">
            <img src={PHOTO_MOVELARIA} alt="Cooperados produzindo móveis na Movelaria Anambé" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="eyebrow !text-copaiba-300 mb-4">Movelaria Anambé</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Dos resíduos da floresta, peças que cruzam o mundo.
            </h2>
            <div className="h-px w-24 bg-copaiba-500 my-7" />
            <p className="text-cream-100/90 leading-relaxed text-lg">
              A <strong>Movelaria Anambé</strong> utiliza os <strong>resíduos de madeira</strong> do manejo florestal
              para a produção de <strong>mesas, cadeiras, armários, portas</strong> e outros objetos artesanais —
              todos fabricados pelos próprios moradores das comunidades da Flona, sempre antenada aos mercados
              <strong> nacional e internacional</strong>.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <DarkHighlight i={Hammer} t="Produção comunitária" />
              <DarkHighlight i={Leaf} t="Aproveitamento total" />
              <DarkHighlight i={Award} t="Design autoral" />
              <DarkHighlight i={Globe2} t="Alcance internacional" />
            </div>
            <Link to="/eco-loja" data-testid="about-movelaria-cta" className="btn-primary bg-copaiba-500 text-forest-900 hover:bg-copaiba-300 mt-10">
              Ver peças na Eco Loja
            </Link>
            <a
              href={whatsappEcoLojaLink("Olá! Gostaria de informações sobre os móveis da Movelaria Anambé.")}
              target="_blank"
              rel="noreferrer"
              data-testid="about-movelaria-whatsapp"
              className="mt-4 inline-flex items-center gap-2 ml-0 md:ml-4 text-cream-100/95 hover:text-copaiba-300 font-medium"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.67 4.07 1.83 5.68L2 22l4.55-1.94c1.55.85 3.32 1.34 5.49 1.34 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.79 14.03c-.24.68-1.42 1.31-1.96 1.35-.5.05-1.14.06-1.83-.12-.42-.11-.96-.29-1.66-.6-2.94-1.27-4.86-4.24-5.01-4.43-.14-.19-1.19-1.58-1.19-3.03 0-1.44.76-2.15 1.03-2.44.27-.29.59-.36.79-.36h.56c.18 0 .43-.07.66.5.24.6.83 2.05.9 2.2.07.14.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.14.14-.29.29-.13.57.17.29.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.27.1 1.71.8 2 .95.29.14.48.22.55.34.07.12.07.69-.17 1.37Z"/></svg>
              WhatsApp: (93) 99117‑7997
            </a>
          </div>
        </div>
      </section>

      {/* Manejo Operacional */}
      <section className="bg-cream-200 py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 aspect-[4/5] overflow-hidden order-2 lg:order-1">
            <img src={COOMFLONA_MANEJO} alt="Manejo operacional na COOMFLONA" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="eyebrow mb-4">Como trabalhamos</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
              Manejo operacional 100% comunitário.
            </h2>
            <div className="section-divider my-7" />
            <p className="text-forest-900/85 leading-relaxed text-lg">
              Manejo operacional é a administração e o controle das atividades da cooperativa para garantir que tudo
              funcione de forma <strong>eficiente, segura e sustentável</strong>. Na COOMFLONA, isso significa planejar e
              executar cada etapa do manejo florestal comunitário de forma responsável, assegurando qualidade, geração de
              renda e preservação da floresta.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              <Highlight i={Leaf} t="Sustentabilidade" />
              <Highlight i={Users} t="Comunidade" />
              <Highlight i={Award} t="Qualidade" />
              <Highlight i={Globe2} t="Rastreabilidade" />
            </div>
          </div>
        </div>
      </section>

      {/* Linha do tempo */}
      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">Trajetória</div>
            <h2 className="font-display text-4xl md:text-5xl text-forest-900 leading-tight">
              Duas décadas cuidando da floresta juntos.
            </h2>
          </div>
          <div className="lg:col-span-8 relative pl-8 border-l border-forest-700/20">
            {[
              { y: "16/02/2005", t: "Fundação da COOMFLONA", d: "Lideranças da Flona do Tapajós se unem, com apoio técnico do Projeto Promanejo, para combater a exploração ilegal e promover o manejo florestal comunitário em Belterra (PA)." },
              { y: "2010", t: "Consolidação do manejo", d: "A cooperativa estrutura sua operação florestal madeireira sustentável e firma parcerias que garantem a comercialização responsável da produção." },
              { y: "2015", t: "Nasce a Movelaria Anambé", d: "Cooperados inauguram a movelaria para transformar resíduos de madeira em móveis e objetos artesanais, ampliando a geração de renda." },
              { y: "2020", t: "Eco Loja Tapajós", d: "Estruturamos o braço de varejo para levar a bioeconomia da floresta — óleo de copaíba, artesanato e móveis — para os mercados nacional e internacional." },
              { y: "2026", t: "21 anos e 305 cooperados", d: "Mais de 1.000 famílias beneficiadas, manejo florestal 100% comunitário e uma nova geração dando continuidade ao trabalho iniciado em 2005." },
            ].map((e, i) => (
              <div key={i} className="relative pb-10 last:pb-0">
                <div className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-copaiba-500 ring-4 ring-cream-100" />
                <div className="eyebrow !text-copaiba-700 mb-1">{e.y}</div>
                <h3 className="font-display text-2xl text-forest-900">{e.t}</h3>
                <p className="text-forest-900/75 mt-1">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeria */}
      <section className="pb-24">
        <div className="container-page grid grid-cols-2 md:grid-cols-3 gap-3">
          <img src={PHOTO_INVENTORY} alt="Equipe em inventário florestal" className="aspect-square object-cover w-full" />
          <img src={PHOTO_BASE_KM117} alt="Base KM 117 vista do alto" className="aspect-square object-cover w-full md:col-span-1 md:row-span-2 md:h-full md:aspect-auto" />
          <img src={PHOTO_MOVELARIA} alt="Movelaria Anambé" className="aspect-square object-cover w-full" />
          <img src={COOMFLONA_TEAM} alt="Equipe COOMFLONA em campo" className="aspect-square object-cover w-full md:col-span-2 md:aspect-auto md:h-[260px]" />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest-700 text-cream-100 py-20">
        <div className="container-page flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl">Conheça nossos produtos.</h2>
            <p className="text-cream-100/80 mt-2">Cada item carrega a assinatura da floresta e das mãos que o fizeram.</p>
          </div>
          <Link to="/eco-loja" data-testid="about-cta-shop" className="btn-primary bg-copaiba-500 text-forest-900 hover:bg-copaiba-300">
            Visitar a Eco Loja
          </Link>
        </div>
      </section>
    </div>
  );
}

function Highlight({ i: Icon, t }) {
  return (
    <div className="flex items-center gap-3 text-sm text-forest-900/85">
      <Icon className="w-5 h-5 text-copaiba-700" />
      {t}
    </div>
  );
}

function DarkHighlight({ i: Icon, t }) {
  return (
    <div className="flex items-center gap-3 text-cream-100/90">
      <Icon className="w-5 h-5 text-copaiba-300" />
      {t}
    </div>
  );
}
